import { Link } from 'react-router-dom'
import SeoHead, { generateFAQSchema } from '../components/SeoHead'
import { isPageSeoEnabled } from '../config/siteConfig'
import './Quality.css'

const Quality = () => {
  const seoEnabled = isPageSeoEnabled('quality')
  const certifications = [
    {
      name: 'GMP Certified',
      fullName: 'Good Manufacturing Practice',
      description: 'Our facility meets stringent GMP requirements ensuring consistent production and quality control.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
        </svg>
      )
    },
    {
      name: 'ISO 9001:2015',
      fullName: 'Quality Management System',
      description: 'Certified quality management system ensuring continuous improvement and customer satisfaction.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
        </svg>
      )
    },
    {
      name: 'WHO-GMP',
      fullName: 'World Health Organization Standards',
      description: 'Compliance with international WHO guidelines for pharmaceutical manufacturing.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/>
        </svg>
      )
    },
    {
      name: 'FSSAI',
      fullName: 'Food Safety Standards Authority',
      description: 'Licensed under FSSAI for food supplement and nutraceutical products.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
        </svg>
      )
    }
  ]

  const qcSteps = [
    {
      step: 1,
      title: 'Raw Material Inspection',
      description: 'Every raw material and API is tested for identity, purity, and potency before acceptance into our manufacturing process.'
    },
    {
      step: 2,
      title: 'In-Process Testing',
      description: 'Continuous monitoring during manufacturing ensures each batch meets specifications at every stage of production.'
    },
    {
      step: 3,
      title: 'Finished Product Analysis',
      description: 'Comprehensive testing of finished products including assay, dissolution, and microbiological parameters.'
    },
    {
      step: 4,
      title: 'Stability Testing',
      description: 'Long-term and accelerated stability studies ensure product quality throughout shelf life.'
    },
    {
      step: 5,
      title: 'Release & Documentation',
      description: 'Complete documentation and quality review before batch release for distribution.'
    }
  ]

  // FAQ Schema for quality questions
  const qualityFaqs = [
    { question: 'Is SIBR Lifescience GMP certified?', answer: 'Yes, SIBR Lifescience is GMP (Good Manufacturing Practice) certified, ensuring our products meet the highest quality standards.' },
    { question: 'What quality certifications does SIBR Lifescience have?', answer: 'We hold GMP, ISO 9001:2015, WHO-GMP, and FSSAI certifications, demonstrating our commitment to pharmaceutical excellence.' },
    { question: 'Where are SIBR Lifescience products manufactured?', answer: 'Our products are manufactured at our state-of-the-art facility in Baddi, Himachal Pradesh, India.' }
  ]
  const faqSchema = generateFAQSchema(qualityFaqs)

  return (
    <div className="quality-page">
      <SeoHead
        title="Quality & Compliance | GMP Certified | SIBR Lifescience"
        description="Discover SIBR Lifescience's commitment to quality. GMP, ISO 9001:2015, and WHO-GMP certified pharmaceutical manufacturing."
        canonical="/quality"
        schema={faqSchema}
        seoEnabled={seoEnabled}
      />
      
      {/* Hero */}
      <section className="page-hero quality-hero">
        <div className="container">
          <h1>Unwavering Commitment to Quality</h1>
          <p>
            Adhering to Global Standards for Every Formulation. Our rigorous quality 
            systems ensure that every product meets the highest standards of safety and efficacy.
          </p>
        </div>
      </section>

      {/* Core Philosophy */}
      <section className="philosophy-section section">
        <div className="container">
          <div className="philosophy-content">
            <span className="section-subtitle">Our Philosophy</span>
            <h2>Quality is Non-Negotiable</h2>
            <p>
              At SIBR Lifescience, quality is not just a department—it's embedded in our DNA. 
              Every decision we make, from sourcing raw materials to final product release, 
              is guided by our patient-first approach. We understand that behind every product 
              is a patient who trusts us with their health and well-being.
            </p>
            <p>
              Our commitment to quality goes beyond regulatory compliance. We continuously 
              invest in advanced testing equipment, train our personnel to the highest standards, 
              and implement robust quality management systems that exceed industry requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="certifications-section section bg-light">
        <div className="container">
          <div className="section-title">
            <span className="section-subtitle">Certifications & Compliance</span>
            <h2>Recognized Quality Standards</h2>
            <p>
              Our manufacturing facility holds multiple certifications demonstrating 
              our commitment to pharmaceutical excellence.
            </p>
          </div>
          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="certification-card">
                <div className="cert-icon">{cert.icon}</div>
                <h3>{cert.name}</h3>
                <span className="cert-fullname">{cert.fullName}</span>
                <p>{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QC Process */}
      <section className="qc-process-section section">
        <div className="container">
          <div className="section-title">
            <span className="section-subtitle">Quality Control</span>
            <h2>Our QC Process</h2>
            <p>
              A systematic approach ensuring quality at every stage of manufacturing.
            </p>
          </div>
          <div className="qc-process">
            {qcSteps.map((step, index) => (
              <div key={index} className="qc-step">
                <div className="step-header">
                  <div className="step-number">{step.step}</div>
                  <h3>{step.title}</h3>
                </div>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory Vigilance */}
      <section className="regulatory-section section bg-light">
        <div className="container">
          <div className="regulatory-grid">
            <div className="regulatory-content">
              <div className="regulatory-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <h2>Regulatory Vigilance</h2>
              <p>
                We maintain continuous vigilance over evolving regulatory landscapes. Our 
                regulatory affairs team stays updated with the latest guidelines from CDSCO, 
                FDA, WHO, and other regulatory bodies to ensure ongoing compliance.
              </p>
              <ul className="regulatory-list">
                <li>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  CDSCO compliance for Indian market
                </li>
                <li>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Regular internal audits and inspections
                </li>
                <li>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Documentation as per Schedule M guidelines
                </li>
                <li>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Pharmacovigilance monitoring
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="quality-cta section-sm">
        <div className="container">
          <div className="quality-cta-content">
            <h2>Questions About Our Quality Standards?</h2>
            <p>
              We're happy to discuss our quality processes and certifications with 
              healthcare professionals and business partners.
            </p>
            <div className="quality-cta-actions">
              <Link to="/contact" className="btn btn-primary">
                Contact Us
              </Link>
              <Link to="/products" className="btn btn-secondary">
                View Our Products
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Quality

